import React, { useEffect, useState } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { ColDef, themeQuartz } from 'ag-grid-community';
import '../../config/agGridConfig'; // Register AG Grid modules
import { adminAPI } from '../../services/api';
import '../../styles/AdminGlobal.css';
import './Admin.css';

const OrderManagement: React.FC = () => {
  const [orders, setOrders] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'preorder' | 'pending' | 'processing' | 'shipped' | 'delivered' | 'cancelled'>('all');
  const [sendingToSteadfast, setSendingToSteadfast] = useState<number | null>(null);
  const [selectedRows, setSelectedRows] = useState<any[]>([]);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState<any>(null);
  
  // Date range filter
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');

  // Status filter tabs configuration
  const statusTabs = [
    { key: 'all', label: 'All Orders' },
    { key: 'preorder', label: 'PreOrder' },
    { key: 'pending', label: 'Pending' },
    { key: 'processing', label: 'Processing' },
    { key: 'shipped', label: 'Shipped' },
    { key: 'delivered', label: 'Delivered' },
    { key: 'cancelled', label: 'Cancelled' },
  ] as const;

  const columnDefs: ColDef[] = [
    { 
      field: 'select',
      headerName: '', 
      checkboxSelection: true,
      headerCheckboxSelection: true,
      width: 50,
      pinned: 'left',
      lockPosition: true,
      suppressMovable: true,
      sortable: false,
      filter: false,
    },
    { 
      field: 'order_number', 
      headerName: 'Order #', 
      width: 130, 
      sortable: true, 
      filter: true,
    },
    { 
      field: 'product_image', 
      headerName: 'Image', 
      width: 100,
      cellRenderer: (params: any) => {
        const firstItem = params.data.items?.[0];
        const imageUrl = firstItem?.product_image || firstItem?.image || '';
        return imageUrl ? (
          <img 
            src={imageUrl} 
            alt="Product" 
            style={{ width: '60px', height: '60px', objectFit: 'cover', borderRadius: '4px' }}
          />
        ) : (
          <div style={{ width: '60px', height: '60px', background: '#f0f0f0', borderRadius: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <span style={{ fontSize: '10px', color: '#999' }}>No Image</span>
          </div>
        );
      }
    },
    { 
      field: 'product_title', 
      headerName: 'Product Title', 
      flex: 1,
      minWidth: 200,
      sortable: true, 
      filter: true,
      valueGetter: (params: any) => {
        const items = params.data.items || [];
        if (items.length === 0) return 'No items';
        if (items.length === 1) return items[0].product_name || items[0].name || 'Unknown';
        return `${items[0].product_name || items[0].name || 'Unknown'} (+${items.length - 1} more)`;
      }
    },
    { 
      field: 'customer_name', 
      headerName: 'Customer', 
      width: 150, 
      sortable: true, 
      filter: true 
    },
    { 
      field: 'customer_phone', 
      headerName: 'Phone', 
      width: 130, 
      sortable: true, 
      filter: true,
      valueGetter: (params: any) => params.data.customer_phone || 'N/A'
    },
    { 
      field: 'shipping_address', 
      headerName: 'Address', 
      width: 200, 
      sortable: true, 
      filter: true,
      cellRenderer: (params: any) => (
        <div style={{ whiteSpace: 'normal', lineHeight: '1.4', padding: '4px 0' }}>
          {params.value || '-'}
        </div>
      )
    },
    { 
      field: 'notes', 
      headerName: 'Notes', 
      width: 150, 
      sortable: true,
      cellRenderer: (params: any) => (
        <div style={{ whiteSpace: 'normal', lineHeight: '1.4', padding: '4px 0' }}>
          {params.value || '-'}
        </div>
      )
    },
    { 
      field: 'total_amount', 
      headerName: 'Total', 
      width: 110, 
      sortable: true,
      valueFormatter: (p: any) => `৳${parseFloat(p.value || 0).toFixed(2)}`
    },
    { 
      field: 'shipping_fees', 
      headerName: 'Shipping', 
      width: 100, 
      sortable: true,
      valueGetter: () => '৳60.00' // Fixed shipping cost
    },
    { 
      field: 'created_at', 
      headerName: 'Order Date', 
      width: 160, 
      sortable: true,
      valueFormatter: (p: any) => {
        if (!p.value) return '';
        const date = new Date(p.value);
        return date.toLocaleString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        });
      }
    },
    { 
      field: 'status', 
      headerName: 'Status', 
      width: 120,
      sortable: true,
      cellRenderer: (params: any) => (
        <span className={`status-badge ${params.value?.toLowerCase() || 'pending'}`}>
          {params.value || 'Pending'}
        </span>
      )
    },
    { 
      field: 'steadfast_tracking_code', 
      headerName: 'Steadfast Tracking', 
      width: 180,
      sortable: true,
      cellRenderer: (params: any) => {
        const trackingCode = params.data.steadfast_tracking_code;
        const status = params.data.steadfast_status;
        if (trackingCode) {
          return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <span style={{ fontWeight: 'bold', fontSize: '11px' }}>{trackingCode}</span>
              {status && <span style={{ fontSize: '10px', color: '#666' }}>{status}</span>}
            </div>
          );
        }
        return <span style={{ color: '#999', fontSize: '11px' }}>Not sent</span>;
      }
    },
    { 
      headerName: 'Actions',
      width: 180,
      pinned: 'right',
      lockPosition: true,
      suppressMovable: true,
      cellRenderer: (params: any) => {
        const hasSteadfast = params.data.steadfast_consignment_id;
        const isSending = sendingToSteadfast === params.data.id;
        
        const buttonStyle = {
          border: 'none',
          borderRadius: '6px',
          width: '32px',
          height: '32px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          fontSize: '16px',
          transition: 'all 0.2s ease',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
        };
        
        return (
          <div style={{ display: 'flex', gap: '6px', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
            {!hasSteadfast ? (
              <button 
                onClick={() => handleSendToSteadfast(params.data.id)}
                disabled={isSending}
                title={isSending ? 'Sending to Steadfast...' : 'Send to Steadfast'}
                style={{ 
                  ...buttonStyle,
                  backgroundColor: isSending ? '#95d5b2' : '#28a745',
                  color: 'white',
                  opacity: isSending ? 0.7 : 1,
                  cursor: isSending ? 'not-allowed' : 'pointer'
                }}
                onMouseEnter={(e) => !isSending && (e.currentTarget.style.transform = 'scale(1.1)')}
                onMouseLeave={(e) => (e.currentTarget.style.transform = 'scale(1)')}
              >
                {isSending ? '⏳' : '📦'}
              </button>
            ) : (
              <button 
                onClick={() => handleViewTracking(params.data.id, params.data.steadfast_consignment_id)}
                title="Track Order"
                style={{ 
                  ...buttonStyle,
                  backgroundColor: '#17a2b8',
                  color: 'white'
                }}
                onMouseEnter={(e) => (e.currentTarget.style.transform = 'scale(1.1)')}
                onMouseLeave={(e) => (e.currentTarget.style.transform = 'scale(1)')}
              >
                🔍
              </button>
            )}
            <button 
              onClick={() => handleEditOrder(params.data)}
              title="Edit Order"
              style={{ 
                ...buttonStyle,
                backgroundColor: '#007bff',
                color: 'white'
              }}
              onMouseEnter={(e) => (e.currentTarget.style.transform = 'scale(1.1)')}
              onMouseLeave={(e) => (e.currentTarget.style.transform = 'scale(1)')}
            >
              ✏️
            </button>
            <button 
              onClick={() => handleViewOrder(params.data)}
              title="View Details"
              style={{ 
                ...buttonStyle,
                backgroundColor: '#6c757d',
                color: 'white'
              }}
              onMouseEnter={(e) => (e.currentTarget.style.transform = 'scale(1.1)')}
              onMouseLeave={(e) => (e.currentTarget.style.transform = 'scale(1)')}
            >
              👁️
            </button>
            <button 
              onClick={() => handleDeleteOrder(params.data.id)}
              title="Delete Order"
              style={{ 
                ...buttonStyle,
                backgroundColor: '#dc3545',
                color: 'white'
              }}
              onMouseEnter={(e) => (e.currentTarget.style.transform = 'scale(1.1)')}
              onMouseLeave={(e) => (e.currentTarget.style.transform = 'scale(1)')}
            >
              🗑️
            </button>
          </div>
        );
      }
    }
  ];

  useEffect(() => {
    loadOrders();
  }, []);

  // Listen for refresh event from AdminLayout header
  useEffect(() => {
    const handleRefresh = () => loadOrders();
    window.addEventListener('admin-refresh', handleRefresh);
    return () => window.removeEventListener('admin-refresh', handleRefresh);
  }, []);

  const loadOrders = async () => {
    try {
      setLoading(true);
      const response = await adminAPI.getOrders();
      const data = response.data || response;
      const ordersList = Array.isArray(data) ? data : (data.results || []);
      console.log('Orders loaded:', ordersList.length, 'First order:', ordersList[0]);
      setOrders(ordersList);
    } catch (error) {
      console.error('Failed to load orders:', error);
      setOrders([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSendToSteadfast = async (orderId: number) => {
    if (!window.confirm('Are you sure you want to send this order to Steadfast Courier?')) {
      return;
    }

    setSendingToSteadfast(orderId);
    try {
      const response = await adminAPI.sendOrderToSteadfast(orderId);
      const data = response.data;
      
      if (data.success) {
        alert(`✅ Order sent to Steadfast successfully!\n\nConsignment ID: ${data.consignment_id}\nTracking Code: ${data.tracking_code}`);
        // Reload orders to get updated data
        await loadOrders();
      } else {
        alert(`Failed to send order to Steadfast: ${data.error || 'Unknown error'}`);
      }
    } catch (error: any) {
      console.error('Failed to send order to Steadfast:', error?.response?.status, error?.response?.data || error?.message || error);
      
      // Extract detailed error from response
      const responseData = error.response?.data;
      let errorMessage = 'Failed to send order to Steadfast.';
      
      if (responseData) {
        // Handle case where server returns plain text/HTML instead of JSON
        if (typeof responseData === 'string') {
          // Strip HTML tags if it's an HTML error page
          const cleanText = responseData.replace(/<[^>]*>/g, '').trim().substring(0, 300);
          errorMessage = cleanText || 'Server returned an unexpected error. Check server logs.';
        } else {
          errorMessage = responseData.error || responseData.detail || errorMessage;
        
          // Show validation errors if present
          if (responseData.validation_errors && responseData.validation_errors.length > 0) {
            errorMessage += '\n\nValidation Issues:\n• ' + responseData.validation_errors.join('\n• ');
          }
          
          // Show environment info for debugging
          if (responseData.environment) {
            errorMessage += `\n\nEnvironment: ${responseData.environment}`;
          }
          
          // Show API details if available
          if (responseData.details) {
            const details = responseData.details;
            if (details.message) {
              errorMessage += `\n\nAPI Message: ${details.message}`;
            }
            if (details.errors && typeof details.errors === 'object') {
              const fieldErrors = Object.entries(details.errors)
                .map(([key, val]) => `${key}: ${Array.isArray(val) ? val.join(', ') : val}`)
                .join('\n• ');
              if (fieldErrors) {
                errorMessage += `\n\nField Errors:\n• ${fieldErrors}`;
              }
            }
          }
        }
      } else {
        errorMessage += '\n\n' + (error.message || 'Network error. Please check your connection.');
      }
      
      alert(`❌ ${errorMessage}`);
    } finally {
      setSendingToSteadfast(null);
    }
  };

  const handleEditOrder = (order: any) => {
    // TODO: Implement edit modal/page
    const newStatus = prompt(`Edit Order Status for ${order.order_number}\n\nCurrent Status: ${order.status}\n\nEnter new status (pending/processing/shipped/delivered/cancelled):`);
    
    if (newStatus && ['pending', 'processing', 'shipped', 'delivered', 'cancelled'].includes(newStatus.toLowerCase())) {
      updateOrderStatus(order.id, newStatus.toLowerCase());
    } else if (newStatus) {
      alert('Invalid status. Valid options: pending, processing, shipped, delivered, cancelled');
    }
  };

  const updateOrderStatus = async (orderId: number, status: string) => {
    try {
      await adminAPI.updateOrderStatus(orderId, status);
      alert('Order status updated successfully!');
      await loadOrders();
    } catch (error: any) {
      console.error('Failed to update order status:', error);
      alert(`Error: ${error.response?.data?.error || error.message || 'Failed to update order status'}`);
    }
  };

  const handleViewOrder = (order: any) => {
    setSelectedOrder(order);
    setViewModalOpen(true);
  };

  const closeViewModal = () => {
    setViewModalOpen(false);
    setSelectedOrder(null);
  };

  const handleDeleteOrder = async (orderId: number) => {
    if (!window.confirm('Are you sure you want to delete this order? This action cannot be undone.')) {
      return;
    }

    try {
      // TODO: Implement delete API endpoint
      // await adminAPI.deleteOrder(orderId);
      alert('Delete functionality will be implemented soon.');
      // await loadOrders();
    } catch (error: any) {
      console.error('Failed to delete order:', error);
      alert(`Error: ${error.response?.data?.error || error.message || 'Failed to delete order'}`);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedRows.length === 0) {
      alert('Please select orders to delete');
      return;
    }

    if (!window.confirm(`Are you sure you want to delete ${selectedRows.length} selected order(s)? This action cannot be undone.`)) {
      return;
    }

    try {
      // TODO: Implement bulk delete
      alert('Bulk delete functionality will be implemented soon.');
    } catch (error: any) {
      console.error('Failed to bulk delete orders:', error);
      alert(`Error: ${error.message || 'Failed to delete orders'}`);
    }
  };

  const onSelectionChanged = (event: any) => {
    const selected = event.api.getSelectedRows();
    setSelectedRows(selected);
  };

  const handleViewTracking = async (orderId: number, consignmentId: string) => {
    try {
      const response = await adminAPI.getSteadfastTracking(orderId);
      const data = response.data;
      
      if (data.success) {
        const trackingInfo = data.tracking_data;
        alert(`Steadfast Tracking Info\n\nConsignment ID: ${data.consignment_id}\nTracking Code: ${data.tracking_code}\n\nStatus: ${trackingInfo?.delivery_status || 'N/A'}\nLast Update: ${trackingInfo?.updated_at || 'N/A'}`);
      } else {
        alert(`Failed to get tracking info: ${data.error}`);
      }
    } catch (error: any) {
      console.error('Failed to get tracking info:', error);
      alert(`Error: ${error.response?.data?.error || error.message || 'Failed to get tracking info'}`);
    }
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  // Ensure orders is always an array before filtering
  const ordersArray = Array.isArray(orders) ? orders : [];
  const filteredOrders = ordersArray.filter(order => {
    const matchesSearch = order.order_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.customer_name?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || order.status?.toLowerCase() === statusFilter;
    
    // Date range filtering
    let matchesDate = true;
    if (startDate || endDate) {
      const orderDate = new Date(order.created_at);
      if (startDate) {
        const start = new Date(startDate);
        start.setHours(0, 0, 0, 0);
        if (orderDate < start) matchesDate = false;
      }
      if (endDate) {
        const end = new Date(endDate);
        end.setHours(23, 59, 59, 999);
        if (orderDate > end) matchesDate = false;
      }
    }
    
    return matchesSearch && matchesStatus && matchesDate;
  });

  return (
    <div className="admin-page">
      {/* Single-Row Filter Bar */}
      <div style={{
        background: 'white',
        borderRadius: '10px',
        padding: '0.5rem 0.75rem',
        marginBottom: '1rem',
        boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
        border: '1px solid #f3f4f6',
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem',
        overflowX: 'auto'
      }}>
        {/* Status Filter Tabs - Compact Pills */}
        <div style={{
          display: 'flex',
          gap: '4px',
          overflowX: 'auto',
          paddingBottom: '2px'
        }}>
          {statusTabs.map(tab => (
            <button
              key={tab.key}
              onClick={() => setStatusFilter(tab.key)}
              style={{
                padding: '0.35rem 0.65rem',
                border: statusFilter === tab.key ? '1px solid #0284c7' : '1px solid #e5e7eb',
                borderRadius: '6px',
                background: statusFilter === tab.key ? '#0284c7' : '#ffffff',
                cursor: 'pointer',
                fontSize: '0.75rem',
                fontWeight: statusFilter === tab.key ? '600' : '500',
                color: statusFilter === tab.key ? '#ffffff' : '#6b7280',
                transition: 'all 0.15s ease',
                whiteSpace: 'nowrap',
                letterSpacing: '0.01em'
              }}
              onMouseEnter={(e) => {
                if (statusFilter !== tab.key) {
                  e.currentTarget.style.backgroundColor = '#f9fafb';
                  e.currentTarget.style.borderColor = '#d1d5db';
                  e.currentTarget.style.color = '#374151';
                }
              }}
              onMouseLeave={(e) => {
                if (statusFilter !== tab.key) {
                  e.currentTarget.style.backgroundColor = '#ffffff';
                  e.currentTarget.style.borderColor = '#e5e7eb';
                  e.currentTarget.style.color = '#6b7280';
                }
              }}
            >
              {tab.label}
            </button>
          ))}
          
          {/* Date Range Filter - Compact */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            marginLeft: '8px',
            paddingLeft: '8px',
            borderLeft: '1px solid #e5e7eb'
          }}>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              style={{
                padding: '0.3rem 0.4rem',
                border: '1px solid #e5e7eb',
                borderRadius: '5px',
                fontSize: '0.7rem',
                color: startDate ? '#374151' : '#9ca3af',
                backgroundColor: '#ffffff',
                cursor: 'pointer',
                outline: 'none',
                width: '110px'
              }}
              title="From date"
            />
            <span style={{ color: '#9ca3af', fontSize: '0.7rem', fontWeight: '500' }}>to</span>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              style={{
                padding: '0.3rem 0.4rem',
                border: '1px solid #e5e7eb',
                borderRadius: '5px',
                fontSize: '0.7rem',
                color: endDate ? '#374151' : '#9ca3af',
                backgroundColor: '#ffffff',
                cursor: 'pointer',
                outline: 'none',
                width: '110px'
              }}
              title="To date"
            />
            {(startDate || endDate) && (
              <button
                onClick={() => { setStartDate(''); setEndDate(''); }}
                style={{
                  padding: '0.2rem 0.4rem',
                  border: 'none',
                  borderRadius: '4px',
                  background: '#fee2e2',
                  color: '#dc2626',
                  cursor: 'pointer',
                  fontSize: '0.65rem',
                  fontWeight: '500'
                }}
                title="Clear date filter"
              >
                ✕
              </button>
            )}
          </div>
        </div>

        {/* Separator */}
        <div style={{ width: '1px', height: '20px', background: '#e5e7eb', marginLeft: 'auto' }} />

        {/* Count */}
        <div style={{ 
          fontSize: '0.75rem',
          color: '#6b7280',
          fontWeight: '500',
          whiteSpace: 'nowrap'
        }}>
          <strong style={{ color: '#111827' }}>{filteredOrders.length}</strong> orders
        </div>

        {/* Search */}
        <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
          <span style={{
            position: 'absolute',
            left: '8px',
            color: '#9ca3af',
            fontSize: '12px',
            pointerEvents: 'none'
          }}>🔍</span>
          <input
            type="text"
            placeholder="Search..."
            value={searchTerm}
            onChange={handleSearch}
            style={{
              padding: '0.35rem 0.5rem 0.35rem 1.75rem',
              border: '1px solid #e5e7eb',
              borderRadius: '6px',
              fontSize: '0.75rem',
              width: '120px',
              outline: 'none',
              backgroundColor: '#ffffff'
            }}
          />
        </div>

        {/* Bulk Delete - only show when rows selected */}
        {selectedRows.length > 0 && (
          <button 
            className="btn btn-danger" 
            onClick={handleBulkDelete}
            style={{ 
              padding: '0.35rem 0.6rem',
              borderRadius: '6px',
              fontSize: '0.7rem',
              display: 'flex',
              alignItems: 'center',
              gap: '4px'
            }}
          >
            🗑️ {selectedRows.length}
          </button>
        )}
      </div>
      
      <div className="ag-grid-wrapper">
      
        <div className="ag-grid-container">
          <AgGridReact
            theme={themeQuartz}
            rowData={filteredOrders || []}
            columnDefs={columnDefs}
            pagination={true}
            paginationPageSize={20}
            loading={loading}
            rowHeight={80}
            rowSelection="multiple"
            suppressRowClickSelection={true}
            onSelectionChanged={onSelectionChanged}
            getRowId={(params) => params.data.id.toString()}
            defaultColDef={{
              resizable: true,
            }}
          />
        </div>
      </div>

      {/* Order View Modal */}
      {viewModalOpen && selectedOrder && (
        <div 
          className="modal-overlay" 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000,
            backdropFilter: 'blur(4px)'
          }}
          onClick={closeViewModal}
        >
          <div 
            className="modal-content" 
            style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              width: '90%',
              maxWidth: '800px',
              maxHeight: '90vh',
              display: 'flex',
              flexDirection: 'column',
              boxShadow: '0 10px 40px rgba(0,0,0,0.3)',
              animation: 'modalSlideIn 0.3s ease-out',
              overflow: 'hidden'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header - Fixed */}
            <div style={{
              padding: '24px',
              borderBottom: '2px solid #e9ecef',
              backgroundColor: '#17a2b8',
              color: 'white',
              borderRadius: '12px 12px 0 0',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexShrink: 0
            }}>
              <div>
                <h2 style={{ margin: 0, fontSize: '24px', fontWeight: 'bold' }}>Order Details</h2>
                <p style={{ margin: '4px 0 0', fontSize: '14px', opacity: 0.9 }}>Order #{selectedOrder.order_number}</p>
              </div>
              <button 
                onClick={closeViewModal}
                style={{
                  background: 'rgba(255,255,255,0.2)',
                  border: 'none',
                  borderRadius: '8px',
                  color: 'white',
                  fontSize: '24px',
                  width: '40px',
                  height: '40px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.3)'}
                onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.2)'}
              >
                ×
              </button>
            </div>

            {/* Modal Body - Scrollable */}
            <div 
              className="modal-body-scroll"
              style={{ 
                padding: '24px',
                overflowY: 'auto',
                flex: 1
              }}
            >
              {/* Customer Information */}
              <div style={{ marginBottom: '24px' }}>
                <h3 style={{ 
                  fontSize: '18px', 
                  marginBottom: '12px', 
                  color: '#495057',
                  borderBottom: '2px solid #17a2b8',
                  paddingBottom: '8px',
                  display: 'inline-block'
                }}>
                  Customer Information
                </h3>
                <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '12px' }}>
                  <tbody>
                    <tr style={{ borderBottom: '1px solid #e9ecef' }}>
                      <td style={{ padding: '12px', fontWeight: '600', color: '#6c757d', width: '30%' }}>Name</td>
                      <td style={{ padding: '12px', color: '#212529' }}>{selectedOrder.customer_name}</td>
                    </tr>
                    <tr style={{ borderBottom: '1px solid #e9ecef', backgroundColor: '#f8f9fa' }}>
                      <td style={{ padding: '12px', fontWeight: '600', color: '#6c757d' }}>Phone</td>
                      <td style={{ padding: '12px', color: '#212529' }}>{selectedOrder.customer_phone || 'N/A'}</td>
                    </tr>
                    <tr style={{ borderBottom: '1px solid #e9ecef' }}>
                      <td style={{ padding: '12px', fontWeight: '600', color: '#6c757d' }}>Address</td>
                      <td style={{ padding: '12px', color: '#212529' }}>{selectedOrder.shipping_address}</td>
                    </tr>
                    <tr style={{ backgroundColor: '#f8f9fa' }}>
                      <td style={{ padding: '12px', fontWeight: '600', color: '#6c757d' }}>Status</td>
                      <td style={{ padding: '12px' }}>
                        <span className={`status-badge ${selectedOrder.status?.toLowerCase() || 'pending'}`} style={{ display: 'inline-block' }}>
                          {selectedOrder.status || 'Pending'}
                        </span>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* Order Items */}
              <div style={{ marginBottom: '24px' }}>
                <h3 style={{ 
                  fontSize: '18px', 
                  marginBottom: '12px', 
                  color: '#495057',
                  borderBottom: '2px solid #17a2b8',
                  paddingBottom: '8px',
                  display: 'inline-block'
                }}>
                  Order Items
                </h3>
                <div style={{ overflowX: 'auto', marginTop: '12px' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ backgroundColor: '#17a2b8', color: 'white' }}>
                        <th style={{ padding: '12px', textAlign: 'left', fontSize: '14px' }}>#</th>
                        <th style={{ padding: '12px', textAlign: 'left', fontSize: '14px' }}>Product</th>
                        <th style={{ padding: '12px', textAlign: 'center', fontSize: '14px' }}>Quantity</th>
                        <th style={{ padding: '12px', textAlign: 'right', fontSize: '14px' }}>Price</th>
                        <th style={{ padding: '12px', textAlign: 'right', fontSize: '14px' }}>Subtotal</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(selectedOrder.items || []).map((item: any, idx: number) => (
                        <tr 
                          key={idx} 
                          style={{ 
                            borderBottom: '1px solid #e9ecef',
                            backgroundColor: idx % 2 === 0 ? 'white' : '#f8f9fa'
                          }}
                        >
                          <td style={{ padding: '12px', color: '#6c757d' }}>{idx + 1}</td>
                          <td style={{ padding: '12px', color: '#212529', fontWeight: '500' }}>
                            {item.product_name || item.name || 'Unknown Product'}
                          </td>
                          <td style={{ padding: '12px', textAlign: 'center', color: '#212529' }}>{item.quantity}</td>
                          <td style={{ padding: '12px', textAlign: 'right', color: '#212529' }}>৳{item.price}</td>
                          <td style={{ padding: '12px', textAlign: 'right', color: '#212529', fontWeight: '600' }}>
                            ৳{item.subtotal}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Order Summary */}
              <div style={{ marginBottom: '24px' }}>
                <h3 style={{ 
                  fontSize: '18px', 
                  marginBottom: '12px', 
                  color: '#495057',
                  borderBottom: '2px solid #17a2b8',
                  paddingBottom: '8px',
                  display: 'inline-block'
                }}>
                  Payment Summary
                </h3>
                <table style={{ width: '100%', maxWidth: '400px', marginLeft: 'auto', marginTop: '12px' }}>
                  <tbody>
                    <tr style={{ borderBottom: '1px solid #e9ecef' }}>
                      <td style={{ padding: '12px', fontWeight: '600', color: '#6c757d' }}>Subtotal</td>
                      <td style={{ padding: '12px', textAlign: 'right', color: '#212529' }}>৳{selectedOrder.total_amount}</td>
                    </tr>
                    <tr style={{ borderBottom: '1px solid #e9ecef', backgroundColor: '#f8f9fa' }}>
                      <td style={{ padding: '12px', fontWeight: '600', color: '#6c757d' }}>Shipping Cost</td>
                      <td style={{ padding: '12px', textAlign: 'right', color: '#212529' }}>৳{selectedOrder.shipping_cost || 60}</td>
                    </tr>
                    <tr style={{ backgroundColor: '#17a2b8', color: 'white' }}>
                      <td style={{ padding: '12px', fontWeight: 'bold', fontSize: '16px' }}>Total Amount</td>
                      <td style={{ padding: '12px', textAlign: 'right', fontWeight: 'bold', fontSize: '16px' }}>
                        ৳{selectedOrder.final_amount || (selectedOrder.total_amount + (selectedOrder.shipping_cost || 60))}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* Steadfast Tracking Info */}
              {selectedOrder.steadfast_consignment_id && (
                <div style={{ marginBottom: '24px' }}>
                  <h3 style={{ 
                    fontSize: '18px', 
                    marginBottom: '12px', 
                    color: '#495057',
                    borderBottom: '2px solid #17a2b8',
                    paddingBottom: '8px',
                    display: 'inline-block'
                  }}>
                    Shipping Information
                  </h3>
                  <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '12px' }}>
                    <tbody>
                      <tr style={{ borderBottom: '1px solid #e9ecef' }}>
                        <td style={{ padding: '12px', fontWeight: '600', color: '#6c757d', width: '30%' }}>Consignment ID</td>
                        <td style={{ padding: '12px', color: '#212529' }}>{selectedOrder.steadfast_consignment_id}</td>
                      </tr>
                      <tr style={{ borderBottom: '1px solid #e9ecef', backgroundColor: '#f8f9fa' }}>
                        <td style={{ padding: '12px', fontWeight: '600', color: '#6c757d' }}>Tracking Code</td>
                        <td style={{ padding: '12px', color: '#212529', fontWeight: '600' }}>{selectedOrder.steadfast_tracking_code}</td>
                      </tr>
                      <tr>
                        <td style={{ padding: '12px', fontWeight: '600', color: '#6c757d' }}>Courier Status</td>
                        <td style={{ padding: '12px', color: '#212529' }}>
                          <span style={{ 
                            padding: '4px 12px', 
                            borderRadius: '12px', 
                            backgroundColor: '#d4edda', 
                            color: '#155724',
                            fontSize: '12px',
                            fontWeight: '600'
                          }}>
                            {selectedOrder.steadfast_status || 'In Transit'}
                          </span>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              )}

              {/* Notes */}
              {selectedOrder.notes && (
                <div>
                  <h3 style={{ 
                    fontSize: '18px', 
                    marginBottom: '12px', 
                    color: '#495057',
                    borderBottom: '2px solid #17a2b8',
                    paddingBottom: '8px',
                    display: 'inline-block'
                  }}>
                    Notes
                  </h3>
                  <p style={{ 
                    padding: '12px', 
                    backgroundColor: '#f8f9fa', 
                    borderRadius: '8px', 
                    color: '#495057',
                    marginTop: '12px',
                    lineHeight: '1.6'
                  }}>
                    {selectedOrder.notes}
                  </p>
                </div>
              )}
            </div>

            {/* Modal Footer - Fixed */}
            <div style={{
              padding: '16px 24px',
              borderTop: '1px solid #e9ecef',
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '12px',
              backgroundColor: '#f8f9fa',
              borderRadius: '0 0 12px 12px',
              flexShrink: 0
            }}>
              <button 
                onClick={closeViewModal}
                style={{
                  padding: '10px 24px',
                  backgroundColor: '#6c757d',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#5a6268'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#6c757d'}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrderManagement;
